<script setup lang="ts">
    import { computed, watch, ref, type Ref } from 'vue'
    import { useRoute } from 'vue-router'

    import { MonthPicker, MonthPickerInput } from 'vue-month-picker'

    import { MonthYearHelpers } from '@common/types'
    import type { SchedulingSettingsReadModel } from '@Quest/CorporateScenarios/readmodels'

    import { useSchedulingSettingsQueryV1 } from '@Quest/CorporateScenarios/queries-vue-composables'
    import { useOptimizeCorporateScenarioScheduleCommandV1 } from '@Quest/CorporateScenarios/commands-vue-composables'

    import type { SchedulerDialogViewModel } from '@Quest/CorporateScenarios/Components/SchedulerDialog'
    import type { MonthPickerDate } from '@common/Components/MonthPickerDialog'


    // ======================
    // Props
    // ======================

    const props = defineProps<{ viewModel: SchedulerDialogViewModel }>()
    const emit = defineEmits<{ (e: 'scheduled', result: any): void }>()
    const vm = props.viewModel


    // ======================
    // Route
    // ======================

    const route = useRoute()

    const workspaceId = computed(() => Number(route.params.workspaceId ?? 0))
    const strategyId = computed(() => String(route.params.strategyId ?? ''))
    const scenarioId = computed(() => String(route.params.scenarioId ?? ''))

    const opened = computed(() => vm?.opened.value ?? false)


    // ======================
    // Query
    // ======================

    const {
        runSchedulingSettingsQueryV1,
        modelSchedulingSettingsQueryV1
    } = useSchedulingSettingsQueryV1(workspaceId, strategyId, scenarioId)

    const rm = modelSchedulingSettingsQueryV1 as Ref<SchedulingSettingsReadModel | null>


    // ======================
    // Command
    // ======================

    const {
        invokeOptimizeCorporateScenarioScheduleCommandV1,
        succeedOptimizeCorporateScenarioScheduleCommandV1,
        result: schedulingResult
    } = useOptimizeCorporateScenarioScheduleCommandV1(workspaceId, strategyId, scenarioId)


    // ======================
    // Local State
    // ======================

    const isScheduling = ref(false)
    const show1 = ref(false)
    const show2 = ref(false)

    const scenarioStartDate = ref<Date | null>(null)
    const scenarioEndDate = ref<Date | null>(null)

    const minStartDate = ref<MonthPickerDate | null>(null)
    const minStartDateFrom = ref<Date | null>(null)
    const minStartDateTo = ref<Date | null>(null)

    const maxStartDate = ref<MonthPickerDate | null>(null)
    const maxStartDateFrom = ref<Date | null>(null)
    const maxStartDateTo = ref<Date | null>(null)


    // ======================
    // Derived
    // ======================

    const defaultYear1 = computed(() => minStartDate.value?.year ?? 0)
    const defaultMonth1 = computed(() => minStartDate.value?.monthIndex ?? 0)

    const defaultYear2 = computed(() => maxStartDate.value?.year ?? 0)
    const defaultMonth2 = computed(() => maxStartDate.value?.monthIndex ?? 0)


    // ======================
    // Watch opened
    // ======================

    watch(opened, (val) =>
    {
        if (val === true)
        {
            rm.value = null // 🔥 restore Vue 2 behaviour
            runSchedulingSettingsQueryV1()
        } else
        {
            isScheduling.value = false
            schedulingResult.value = null
        }
    })

    // ======================
    // Watch Query Result
    // ======================

    watch(rm, (current) =>
    {
        if (!current) return

        scenarioStartDate.value = new Date(current.ScenarioStartDateStr)
        scenarioEndDate.value = new Date(current.ScenarioEndDateStr)

        const minStart = new Date(current.MinStartDateStr)
        minStartDate.value = {
            year: minStart.getFullYear(),
            monthIndex: minStart.getMonth() + 1
        }

        minStartDateFrom.value = new Date(
            scenarioStartDate.value.getFullYear(),
            scenarioStartDate.value.getMonth(),
            0, 0, 0, 0, 0
        )

        minStartDateTo.value = new Date(
            scenarioEndDate.value.getFullYear(),
            scenarioEndDate.value.getMonth() + 1,
            0
        )

        const maxStart = new Date(current.MaxStartDateStr)
        maxStartDate.value = {
            year: maxStart.getFullYear(),
            monthIndex: maxStart.getMonth() + 1
        }

        maxStartDateFrom.value = new Date(
            scenarioStartDate.value.getFullYear(),
            scenarioStartDate.value.getMonth(),
            0, 0, 0, 0, 0
        )

        maxStartDateTo.value = new Date(
            scenarioEndDate.value.getFullYear(),
            scenarioEndDate.value.getMonth() + 1,
            0
        )
    })


    // ======================
    // Watch Command Success
    // ======================

    watch(succeedOptimizeCorporateScenarioScheduleCommandV1, (current, previous) =>
    {
        if (current && !previous)
        {
            isScheduling.value = false
            emit('scheduled', schedulingResult.value)
        }
    })


    // ======================
    // Methods
    // ======================

    function onMinStartDateChange(date: MonthPickerDate)
    {
        minStartDate.value = date
    }

    function onMaxStartDateChange(date: MonthPickerDate)
    {
        maxStartDate.value = date
    }

    function handleOptimizeCorporateScenarioScheduleCommand()
    {
        if (!minStartDate.value || !maxStartDate.value) return

        isScheduling.value = true
        schedulingResult.value = null

        const minStartDatePayload = MonthYearHelpers.build(
            minStartDate.value.year!,
            minStartDate.value.monthIndex!
        )

        const maxStartDatePayload = MonthYearHelpers.build(
            maxStartDate.value.year!,
            maxStartDate.value.monthIndex!
        )

        invokeOptimizeCorporateScenarioScheduleCommandV1({
            minStartDate: minStartDatePayload,
            maxStartDate: maxStartDatePayload
        })
    }

    function close()
    {
        vm.close()
    }
</script>

<template>
    <div class="modal-mask" v-if="opened === true">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            Scheduler
                        </h5>
                        <button @click="close" type="button" class="close">&times;</button>
                    </div>

                    <div class="modal-body" style="height: 400px;">
                        <div v-if="rm != null && isScheduling === true">
                            <p class="text-center text-primary my-5">
                                <i class="fa-solid fa-robot fa-beat-fade" style="font-size: 120px;"></i>
                            </p>
                        </div>
                        <div v-if="rm != null && isScheduling === false && schedulingResult != null">
                            <p>Analyzed (unique) portfolios: {{schedulingResult.NumberOfUniquePortfolios}}</p>
                            <p>Found (valid) portfolios: {{schedulingResult.NumberOfValidPortfolios}}</p>
                        </div>

                        <div v-if="rm && isScheduling === false && schedulingResult == null">
                            <div class="form-group">
                                <label>Earliest start date:</label>
                                <div class="input-group">
                                    <input :value="minStartDate ? `${minStartDate.month}, ${minStartDate.year}` : ''" 
                                           type="text" class="form-control pointer bg-white" readonly @click="show1 = !show1" />
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" @click="show1 = !show1">
                                            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <month-picker v-show="show1 === true"
                                              @change="onMinStartDateChange"
                                              :min-date="minStartDateFrom"
                                              :max-date="minStartDateTo"
                                              :default-month="defaultMonth1"
                                              :default-year="defaultYear1"></month-picker>

                            </div>
                            <div class="form-group">
                                <label>Latest start date:</label>
                                <div class="input-group">
                                    <input :value="maxStartDate ? `${maxStartDate.month}, ${maxStartDate.year}` : ''" 
                                           type="text" class="form-control pointer bg-white" readonly @click="show2 = !show2" />
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" @click="show2 = !show2">
                                            <i class="fa-regular fa-calendar" aria-hidden="true"></i>
                                        </button>
                                    </div>
                                </div>
                                <month-picker v-show="show2 === true"
                                              @change="onMaxStartDateChange"
                                              :min-date="maxStartDateFrom"
                                              :max-date="maxStartDateTo"
                                              :default-month="defaultMonth2"
                                              :default-year="defaultYear2"></month-picker>

                            </div>
                        </div>


                    </div>

                    <div class="modal-footer">
                        <button @click="close" type="button" class="btn btn-outline-secondary" :disabled="isScheduling === true">Cancel</button>
                        <button @click="handleOptimizeCorporateScenarioScheduleCommand" type="button" class="btn btn-primary" :disabled="isScheduling === true">
                            <i class="fa-solid fa-robot fa-bounce mr-1"></i> Schedule now
                        </button>
                    </div>

                </div>
            </div>
        </div>
    </div>
</template> 